import type { DataStore } from "../../Data/Store"
import { preserveCalculationProjection } from "../../Data/Connections/Folds"
import type { ApiClient } from "../../Types/Generated/LogbookApi/ApiRuntime"
import type { IStopRequest } from "../../Types/Generated/LogbookApp/CurrentLogApi"
import type { ILogResponse } from "../../Types/Generated/LogbookModel/Log"
import { CruiseApi, ILog, ILogLighting, ISailChange, IShipStatus, TripApi } from "../../Types"
import type { ICruiseCreateResponse } from "../../Types"
import { FocusViewData } from "./Context"

// The write surface FocusView's current-log entry UI calls (the five `current/*`
// endpoints). `recordLog` commits the live current log (the id-0 "now" entry) into a
// real timeline log; engine/lighting/sails append their own status-change log. Each
// resolves once the write settles so the caller can chain a UI transition; the concrete
// data update is owned by the writer (the New variant's store optimism + reconcile, or
// the Legacy variant's own `updateData`/`setN` merge), never the caller.
export interface FocusWriters {
  recordLog: (status?: IShipStatus) => Promise<void>;
  recordEngine: (engineId: string, on: boolean) => Promise<void>;
  recordLighting: (lighting: ILogLighting) => Promise<void>;
  recordSails: (changes: ISailChange[]) => Promise<void>;
  recordStop: (values: IStopRequest) => Promise<void>;
  createCruise: () => Promise<ICruiseCreateResponse>;
  createTrip: () => Promise<ICruiseCreateResponse>;
}

// Identifiers a store-backed FocusView writer bundle is scoped to: the day the
// committed log lands on, so the optimistic temp row is owner-FK scoped to today's
// timeline the way the reconciled server row will be. Undefined until the frame loads;
// the optimistic snapshot then falls back to the current log's own day.
export interface FocusWriterIds {
  dayId: number | undefined;
}

export function logResponseBundle(response: ILogResponse) {
  return { entities: { Log: response.logs?.length ? response.logs : [response.log], Status: response.statuses } };
}

// Monotonic negative temp ids for the create-commit's optimistic row, so an in-flight
// `+ Log` never collides with a real (positive) server id or a second pending commit.
// Module-level so it stays unique across writer instances/renders.
let tempSeq = 0;
function nextTempId(): number {
  tempSeq += 1;
  return -tempSeq;
}

// The day an optimistic committed row is scoped to: today's day id, or -- while the frame
// hasn't loaded and today isn't known yet -- the day the current log itself is on. Naming
// the rule keeps the fallback out of an inline `??` at the point of use.
function scopeDayId(dayTodayId: number | undefined, current: ILog): number {
  return dayTodayId ?? current.dayId;
}

// Drop the client-only `kind` brand so the optimistic row enters the committed `Log`
// entity store as a plain committed log (not a mis-tagged current/provisional one).
function committedSnapshot(current: ILog, dayTodayId: number | undefined, tempId: number): ILog {
  const { kind: _kind, ...base } = current as ILog & { kind?: string };
  return { ...base, id: tempId, dayId: scopeDayId(dayTodayId, current) };
}

// New (flag-on) FocusView writers: each write goes through `DataStore.mutate`, so the
// committed timeline updates optimistically (for `+ Log`, via the temp-id create →
// server-id remap branch: an optimistic committed snapshot of the current log lands at
// once, the `provisional` temp id is dropped once the authoritative `ILogResponse.log`
// reconciles). Engine/lighting/sails carry no optimistic pre-image (their log's fields
// aren't known until the response), so they only reconcile the returned log. The
// response's `statuses` (the created/updated status rows #1142 now carries inline, e.g. a
// manual engine start's engine status) reconcile into the `Status` store in the same pass,
// so the client no longer waits on a separate `Status` WS broadcast. The `CurrentLog`
// store's "now" entry is refreshed by the next `Log/Current` broadcast.
export function createStoreFocusWriters(store: DataStore, ids: FocusWriterIds): FocusWriters {
  const { dayId } = ids;
  const commit = (request: (client: ApiClient) => Promise<ILogResponse>, current?: ILog): Promise<void> => {
    const tempId = nextTempId();
    const optimistic = current ? { upsert: { Log: [committedSnapshot(current, dayId, tempId)] } } : undefined;
    const provisional = current ? { Log: [tempId] } : undefined;
    return store
      .mutate<ILogResponse>({
        optimistic,
        provisional,
        request,
        reconcile: logResponseBundle,
      })
      .then(() => undefined);
  };
  const create = (request: (client: ApiClient) => Promise<ICruiseCreateResponse>) => {
    // A live row that replaces either reference while the request is pending wins.
    const cruises = new Map(store.getQuery("Cruise", {}).map((row) => [row.id, row]));
    const trips = new Map(store.getQuery("Trip", {}).map((row) => [row.id, row]));
    return store.mutate({
      request,
      reconcile: (response) => {
        const currentCruise = response.cruise
          ? store.getEntity("Cruise", response.cruise.id)
          : undefined;
        const currentTrip = response.trip
          ? store.getEntity("Trip", response.trip.id)
          : undefined;
        return {
          entities: {
            Cruise: response.cruise && currentCruise === cruises.get(response.cruise.id)
              ? [preserveCalculationProjection(response.cruise, currentCruise)]
              : [],
            Trip: response.trip && currentTrip === trips.get(response.trip.id)
              ? [preserveCalculationProjection(response.trip, currentTrip)]
              : [],
          },
        };
      },
    });
  };
  return {
    recordLog: (status) => commit((client) => FocusViewData.recordLog(client, status), store.getCurrentLog()),
    recordEngine: (engine, on) => commit((client) => FocusViewData.recordEngine(client, engine, on)),
    recordLighting: (lighting) => commit((client) => FocusViewData.recordLighting(client, lighting)),
    recordSails: (changes) => commit((client) => FocusViewData.recordSails(client, changes)),
    recordStop: (values) => commit((client) => FocusViewData.recordStop(client, values)),
    createCruise: () => create((client) => new CruiseApi(client).cruises.create({ params: {} }).call()),
    createTrip: () => create((client) => new TripApi(client).trips.create({ params: {} }).call()),
  };
}

// Inert FocusView writers for harnesses/tests that render the focus tree but never
// exercise a write. Each rejects loudly if unexpectedly called, so an accidental record
// in a read-only fixture surfaces rather than silently no-opping.
const rejectNoop = (name: string) => (): Promise<never> =>
  Promise.reject(new Error(`noopFocusWriters.${name}: no writer provided (read-only FocusView context)`));

export const noopFocusWriters: FocusWriters = {
  recordLog: rejectNoop("recordLog"),
  recordEngine: rejectNoop("recordEngine"),
  recordLighting: rejectNoop("recordLighting"),
  recordSails: rejectNoop("recordSails"),
  recordStop: rejectNoop("recordStop"),
  createCruise: rejectNoop("createCruise"),
  createTrip: rejectNoop("createTrip"),
};
